import {inject, Injectable} from '@angular/core';
import {Data, Network, Options} from 'vis-network';
import {forkJoin, map, Observable, of} from 'rxjs';
import {VisjsJson} from 'src/app/common/models/ai-reports/shared/assets';
import {ClientAssetService} from 'src/app/services/AIReports/client-asset-service';

@Injectable({providedIn: 'root'})
export class VisjsRenderService {
  private readonly clientAssetService = inject(ClientAssetService);
  private blobUrls: string[] = [];

  /**
   * Replace cross-origin node image URLs with same-origin blob URLs.
   */
  resolveNodeImages(data: VisjsJson): Observable<VisjsJson> {
    const imageNodes = data.nodes.filter(
      (n): n is typeof n & {image: string} => !!n.image
    );
    if (!imageNodes.length) return of(data);

    return forkJoin(
      Object.fromEntries(
        imageNodes.map((n) => [
          String(n.id),
          this.clientAssetService.downloadImageAsObjectUrl(n.image),
        ])
      )
    ).pipe(
      map((blobMap) => {
        this.blobUrls.push(...Object.values(blobMap));
        return {
          ...data,
          nodes: data.nodes.map((n) => ({
            ...n,
            image: n.image ? (blobMap[String(n.id)] ?? n.image) : n.image,
          })),
        };
      })
    );
  }

  /** Register an externally-created blob URL for cleanup on revokeBlobUrls(). */
  trackBlobUrl(url: string): void {
    this.blobUrls.push(url);
  }

  /** Revoke all blob URLs created by resolveNodeImages. */
  revokeBlobUrls(): void {
    this.blobUrls.forEach((url) => URL.revokeObjectURL(url));
    this.blobUrls = [];
  }

  /** Build vis-network options from graph data and container dimensions. */
  buildOptions(data: VisjsJson, width: string, height: string): Options {
    const hasPositions = data.nodes.every(
      (n) => typeof n?.x === 'number' && typeof n?.y === 'number'
    );

    return {
      height,
      width,
      edges: {
        length: 300,
        smooth: false,
      },
      layout: hasPositions
        ? {hierarchical: {enabled: false}}
        : {
            improvedLayout: true,
            hierarchical: {
              enabled: true,
              direction: 'UD',
              sortMethod: 'directed',
              parentCentralization: true,
              levelSeparation: 200,
              nodeSpacing: 200,
              shakeTowards: 'roots',
              blockShifting: true,
              edgeMinimization: true,
            },
          },
      physics: {enabled: false},
      interaction: {dragNodes: true, dragView: true, zoomView: true},
    };
  }

  // vis-network's zoom() clamps to 0.00001 with no Options-level floor, so we enforce one relative to the fit scale.
  private static readonly MIN_ZOOM_FACTOR = 0.5;

  /** Create a vis-network instance inside the given container. */
  createNetwork(container: HTMLDivElement, data: VisjsJson, options: Options): Network {
    const network = new Network(container, data as Data, options);

    // network.destroy() deletes internal structures fit()/getScale() dereference; guard the rAF against a destroy that lands first.
    let destroyed = false;
    network.on('destroy' as Parameters<Network['on']>[0], () => { destroyed = true; });

    let minZoom = 0;
    requestAnimationFrame(() => {
      if (destroyed) return;
      network.fit({animation: false});
      minZoom = network.getScale() * VisjsRenderService.MIN_ZOOM_FACTOR;
    });

    network.on('zoom', ({scale}) => {
      if (minZoom && scale < minZoom) {
        network.moveTo({scale: minZoom, animation: false});
      }
    });

    return network;
  }

  /**
   * Capture a preview PNG from the vis.js canvas, compositing the background image underneath.
   */
  async capturePreview(container: HTMLDivElement, backgroundUrl: string | null | undefined): Promise<Blob | null> {
    const visjsCanvas = container.querySelector('canvas');
    if (!visjsCanvas) return null;

    const output = document.createElement('canvas');
    output.width = visjsCanvas.width;
    output.height = visjsCanvas.height;
    const ctx = output.getContext('2d')!;

    if (backgroundUrl) {
      await new Promise<void>((resolve) => {
        const img = new Image();
        img.crossOrigin = 'use-credentials';
        img.onload = () => {
          ctx.drawImage(img, 0, 0, output.width, output.height);
          resolve();
        };
        img.onerror = () => resolve();
        img.src = backgroundUrl;
      });
    }

    ctx.drawImage(visjsCanvas, 0, 0);

    return new Promise<Blob | null>((resolve) => {
      output.toBlob((blob) => resolve(blob), 'image/png');
    });
  }

  /** Merge live node positions from the network back into the original data. */
  mergePositions(original: VisjsJson, network: Network): VisjsJson {
    const positions = network.getPositions();
    const nodes = original.nodes.map((n) => ({
      ...n,
      x: Math.round(positions[n.id]?.x ?? n.x),
      y: Math.round(positions[n.id]?.y ?? n.y),
    })) as VisjsJson['nodes'];
    return {...original, nodes};
  }
}
